import { useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { DiscoveryList } from '../components/Discoveries';
import { useCampaign, useCampaignId, useNoteIndex, useTypes } from '../hooks';
import { t } from '../i18n';

/** Spieler ohne sichtbare Startseite: zuletzt aktualisierte Notizen, die sie sehen dürfen. */
function PlayerHome({ cid, name, screenToken }: { cid: string; name: string; screenToken: string | null | undefined }) {
  const index = useNoteIndex(cid);
  const types = useTypes(cid);
  const recent = useMemo(() => [...(index.data ?? [])].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 20), [index.data]);
  return (
    <div className="page-narrow">
      <h1>{name}</h1>
      <p className="muted">{t('player.welcome')}</p>
      {screenToken && (
        <p>
          <a className="button" href={`/screen/${screenToken}`} target="_blank" rel="noreferrer">
            {t('player.screen')} ↗
          </a>
        </p>
      )}
      <DiscoveryList cid={cid} limit={10} />
      <section className="card">
        <h2>{t('player.recent')}</h2>
        {index.data && recent.length === 0 && <p className="muted">{t('player.nothing')}</p>}
        <ul className="recent-list">
          {recent.map((n) => (
            <li key={n.id}>
              <span className="type-dot" style={{ background: types.byKey.get(n.typeKey)?.color }} />
              <Link to={`/c/${cid}/n/${n.id}`}>{n.title}</Link>
              <span className="muted small">{new Date(n.updatedAt).toLocaleDateString('de-DE')}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

/** Eigene Seite „Entdeckt“ (Spieler, deren Startseite freigegeben ist, landen sonst dort). */
export function DiscoveriesPage() {
  const cid = useCampaignId();
  return (
    <div className="page-narrow">
      <DiscoveryList cid={cid} />
    </div>
  );
}

/** Öffnet die Startseite der Kampagne, falls vorhanden. */
export function CampaignHome() {
  const cid = useCampaignId();
  const campaign = useCampaign(cid);
  if (!campaign.data) return <div className="muted pad">{t('common.loading')}</div>;
  if (campaign.data.homeNoteId) return <Navigate to={`/c/${cid}/n/${campaign.data.homeNoteId}`} replace />;
  if (campaign.data.role === 'player') return <PlayerHome cid={cid} name={campaign.data.name} screenToken={campaign.data.screenToken} />;
  return (
    <div className="page-narrow">
      <h1>{campaign.data.name}</h1>
      <p className="muted">{t('campaigns.empty')}</p>
      <p className="muted">
        <kbd>Strg K</kbd> {t('nav.quick')}
      </p>
    </div>
  );
}
